import { estimateTopicCardDwellMs } from "@repo/topic-kernel/timeline";
import type { TopicBlockContent } from "@saas/shared/lib/topic-content/topic-content-types";
import {
	type ResolveTopicCardSegmentDurationForCardInput,
	resolveTopicCardSegmentDurationMsForNarrationCard,
	type TopicSegmentDurationCardContext,
} from "./resolve-topic-card-segment-duration-ms";

export interface TopicCardDurationInput {
	id: string;
	blocks?: TopicBlockContent[];
	isPresentationOnly?: boolean;
	layoutTemplate?: string | null;
}

export interface GetTopicCardRequiredDurationMsOptions {
	/** Landing / fixtures: fixed ms per card id. */
	getDemoCardRequiredDurationMs?: (cardId: string) => number | undefined;
	/** When set, narration-eligible cards use the central segment duration resolver. */
	narrationSegmentDuration?: Omit<
		ResolveTopicCardSegmentDurationForCardInput,
		"card"
	> & {
		card: TopicCardDurationInput & TopicSegmentDurationCardContext;
	};
}

/**
 * Autoplay interval for one topic card (reading estimate / demo overrides).
 * Continuation (“Next” ring) dwell is resolved separately via topic continuation settings.
 */
export function getTopicCardRequiredDurationMs(
	card: TopicCardDurationInput | undefined,
	options?: GetTopicCardRequiredDurationMsOptions,
): number {
	if (
		card?.isPresentationOnly ||
		card?.layoutTemplate === "cover" ||
		card?.layoutTemplate === "close"
	) {
		return 0;
	}
	if (card?.id && options?.getDemoCardRequiredDurationMs) {
		const demoMs = options.getDemoCardRequiredDurationMs(card.id);
		if (typeof demoMs === "number" && demoMs > 0) {
			return demoMs;
		}
	}
	const narrationSegmentMs = options?.narrationSegmentDuration
		? resolveTopicCardSegmentDurationMsForNarrationCard(
				options.narrationSegmentDuration,
			)
		: null;
	if (narrationSegmentMs != null && narrationSegmentMs > 0) {
		return narrationSegmentMs;
	}
	return estimateTopicCardDwellMs(card?.blocks);
}
